import { Category } from '../types/Category';

/** Explicit fetch state for lazy-loaded category tree data. */
export type CategoryLoadState = 'unloaded' | 'loading' | 'loaded' | 'error';

/** Thrown (and surfaced to the user) when code expects loaded data that is missing or inconsistent. */
export class CategoryLoadFault extends Error {
  readonly categoryId: string;

  constructor(message: string, categoryId: string) {
    super(message);
    this.name = 'CategoryLoadFault';
    this.categoryId = categoryId;
  }
}

/** Legacy / fully-hydrated trees from {@link fetchCategoryWithChildren} omit load flags — treat as loaded. */
export function isLegacyFullyLoadedCategory(cat: Category): boolean {
  return cat.childrenLoadState === undefined && cat.contentsLoadState === undefined;
}

export function branchChildrenAreLoaded(cat: Category): boolean {
  if (cat.contentCategory) return true;
  if (cat.childrenLoadState !== undefined) {
    return cat.childrenLoadState === 'loaded';
  }
  // Legacy nodes omit load-state: only treat as loaded when the children array is present.
  return cat.children !== undefined;
}

export function categoryContentsAreLoaded(cat: Category): boolean {
  if (!cat.contentCategory) return true;
  if (cat.contentsLoadState !== undefined) {
    return cat.contentsLoadState === 'loaded';
  }
  // Legacy nodes omit load-state: only treat as loaded when the contents array is present.
  // `contents: undefined` with no load-state means not fetched yet (not "empty").
  return cat.contents !== undefined;
}

export function categoryShowsBranchExpandable(cat: Category): boolean {
  if (cat.contentCategory) return false;
  if (cat.childrenLoadState === 'loading') return true;
  if (branchChildrenAreLoaded(cat)) {
    return (cat.children?.length ?? 0) > 0;
  }
  return cat.hasChildCategories === true;
}

/** Validates branch load invariants; throws {@link CategoryLoadFault} on mismatch (never silent). */
export function validateBranchLoadInvariant(cat: Category, context: string): void {
  if (cat.contentCategory) return;
  const state = cat.childrenLoadState ?? 'loaded';
  if (state === 'loaded') {
    if (cat.children === undefined) {
      throw new CategoryLoadFault(
        `${context}: "${cat.name}" (${cat.id}) is marked loaded but subcategories are missing`,
        cat.id,
      );
    }
    if (cat.hasChildCategories === true && cat.children.length === 0) {
      throw new CategoryLoadFault(
        `${context}: "${cat.name}" (${cat.id}) should have subcategories but none were loaded`,
        cat.id,
      );
    }
  }
  if (state === 'unloaded' && cat.children !== undefined && cat.children.length > 0) {
    throw new CategoryLoadFault(
      `${context}: "${cat.name}" (${cat.id}) is unloaded but already has subcategory data`,
      cat.id,
    );
  }
}

/** Validates content-row load invariants; throws {@link CategoryLoadFault} on mismatch. */
export function validateContentsLoadInvariant(cat: Category, context: string): void {
  if (!cat.contentCategory) return;
  const state = cat.contentsLoadState ?? 'loaded';
  if (state === 'loaded') {
    if (cat.contents === undefined) {
      throw new CategoryLoadFault(
        `${context}: "${cat.name}" (${cat.id}) is marked loaded but content rows are missing`,
        cat.id,
      );
    }
    if (cat.hasContents === true && cat.contents.length === 0) {
      throw new CategoryLoadFault(
        `${context}: "${cat.name}" (${cat.id}) should have content rows but none were loaded`,
        cat.id,
      );
    }
  }
  if (state === 'unloaded' && cat.contents !== undefined && cat.contents.length > 0) {
    throw new CategoryLoadFault(
      `${context}: "${cat.name}" (${cat.id}) is unloaded but already has content rows`,
      cat.id,
    );
  }
}

export function assertBranchChildrenLoaded(cat: Category, context: string): void {
  if (cat.contentCategory) return;
  const state = cat.childrenLoadState ?? 'loaded';
  if (state === 'loading') {
    throw new CategoryLoadFault(
      `${context}: subcategories for "${cat.name}" are still loading`,
      cat.id,
    );
  }
  if (state === 'error') {
    throw new CategoryLoadFault(
      `${context}: subcategories for "${cat.name}" failed to load${cat.loadError ? `: ${cat.loadError}` : ''}`,
      cat.id,
    );
  }
  if (state === 'unloaded') {
    throw new CategoryLoadFault(
      `${context}: subcategories for "${cat.name}" have not been loaded`,
      cat.id,
    );
  }
  validateBranchLoadInvariant(cat, context);
}

export function assertCategoryContentsLoaded(cat: Category, context: string): void {
  if (!cat.contentCategory) return;
  const state = cat.contentsLoadState ?? 'loaded';
  if (state === 'loading') {
    throw new CategoryLoadFault(
      `${context}: content rows for "${cat.name}" are still loading`,
      cat.id,
    );
  }
  if (state === 'error') {
    throw new CategoryLoadFault(
      `${context}: content rows for "${cat.name}" failed to load${cat.loadError ? `: ${cat.loadError}` : ''}`,
      cat.id,
    );
  }
  if (state === 'unloaded') {
    throw new CategoryLoadFault(
      `${context}: content rows for "${cat.name}" have not been loaded`,
      cat.id,
    );
  }
  validateContentsLoadInvariant(cat, context);
}

/** Apply server load hints from GET /categories and /categories/subcategories. */
export function annotateCategoryFromApi(raw: Category): Category {
  return {
    ...raw,
    hasChildCategories: raw.hasChildCategories ?? false,
    hasContents: raw.hasContents ?? false,
  };
}

/** One-level child stub after a branch fetch (subcategories known, deeper levels not fetched). */
export function stubBranchChild(child: Category): Category {
  const base = annotateCategoryFromApi(child);
  if (base.contentCategory) {
    return {
      ...base,
      contents: undefined,
      contentsLoadState: base.hasContents ? 'unloaded' : 'loaded',
      ...(base.hasContents ? {} : { contents: [] }),
    };
  }
  return {
    ...base,
    children: undefined,
    childrenLoadState: base.hasChildCategories ? 'unloaded' : 'loaded',
    ...(base.hasChildCategories ? {} : { children: [] }),
  };
}

export function markBranchLoaded(cat: Category, children: Category[]): Category {
  const next: Category = {
    ...cat,
    children,
    childrenLoadState: 'loaded',
    loadError: undefined,
  };
  validateBranchLoadInvariant(next, 'markBranchLoaded');
  return next;
}

export function markContentsLoaded(cat: Category, contents: Category['contents']): Category {
  const rows = contents ?? [];
  const next: Category = {
    ...cat,
    contents: rows,
    contentsLoadState: 'loaded',
    loadError: undefined,
  };
  validateContentsLoadInvariant(next, 'markContentsLoaded');
  return next;
}

/** Recursively mark every node loaded (after {@link fetchCategoryWithChildren}). */
export function markTreeFullyLoaded(cat: Category): Category {
  if (cat.contentCategory) {
    const contents = cat.contents ?? [];
    return markContentsLoaded({ ...cat, hasContents: contents.length > 0 }, contents);
  }
  const children = (cat.children ?? []).map(markTreeFullyLoaded);
  return markBranchLoaded(
    { ...cat, hasChildCategories: children.length > 0 },
    children,
  );
}

/** True when this node and every descendant have branch/content data loaded. */
export function subtreeFullyLoaded(cat: Category): boolean {
  if (cat.contentCategory) {
    return categoryContentsAreLoaded(cat);
  }
  if (!branchChildrenAreLoaded(cat)) {
    return false;
  }
  return (cat.children ?? []).every(subtreeFullyLoaded);
}

/** Categories under {@code root} that still need branch or content data before display. */
export function collectUnloadedNodesInSubtree(root: Category): Category[] {
  const missing: Category[] = [];
  const walk = (cat: Category) => {
    if (cat.contentCategory) {
      if (!categoryContentsAreLoaded(cat)) {
        missing.push(cat);
      }
      return;
    }
    if (!branchChildrenAreLoaded(cat)) {
      missing.push(cat);
      return;
    }
    (cat.children ?? []).forEach(walk);
  };
  walk(root);
  return missing;
}

/** Throws {@link CategoryLoadFault} when a subtree is shown before every descendant is loaded. */
export function assertSubtreeFullyLoadedForDisplay(root: Category, context: string): void {
  const missing = collectUnloadedNodesInSubtree(root);
  if (missing.length > 0) {
    const first = missing[0];
    throw new CategoryLoadFault(
      `${context}: "${root.name}" subtree is incomplete — "${first.name}" is not fully loaded`,
      first.id,
    );
  }
}

export function collectAllCategoryIdsFromRoots(roots: Category[]): Set<string> {
  const ids = new Set<string>();
  const walk = (c: Category) => {
    ids.add(c.id);
    c.children?.forEach(walk);
  };
  roots.forEach(walk);
  return ids;
}

/** Merge saved expansion with a lazy-loaded tree; always includes the document root. */
export function expandedNodesForLoadedTree(
  saved: string[] | undefined,
  rootCategory: Category,
): string[] {
  const valid = collectAllCategoryIdsFromRoots([rootCategory]);
  const next = new Set<string>();
  if (rootCategory.id) next.add(rootCategory.id);
  if (saved?.length) {
    for (const id of saved) {
      if (valid.has(id)) next.add(id);
    }
  }
  return Array.from(next);
}

/** Parent category ids from {@code category} up to the root (uses {@code parentId} links). */
export function resolveParentCategoryIds(
  category: Category,
  findInTree: (id: string) => Category | null,
): string[] {
  const parentIds: string[] = [];
  let parentId = category.parentId;
  while (parentId) {
    parentIds.unshift(parentId);
    const parent = findInTree(parentId);
    parentId = parent?.parentId ?? null;
  }
  return parentIds;
}

export function findCategoryPathInTree(roots: Category[], targetId: string): Category[] | null {
  const walk = (cat: Category, path: Category[]): Category[] | null => {
    const next = [...path, cat];
    if (cat.id === targetId) return next;
    if (cat.children) {
      for (const ch of cat.children) {
        const found = walk(ch, next);
        if (found) return found;
      }
    }
    return null;
  };
  for (const root of roots) {
    const found = walk(root, []);
    if (found) return found;
  }
  return null;
}
